import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { updateOrder } from "@/api/orders";
import { updateOrderTemplate } from "@/api/orderTemplates";
import {
  formatLocality,
  normaliseLocalityStatus,
} from "@/features/orders/lib/locality";
import { toastId, toastSuccess } from "@/lib/toast";
import type { LocalityRef } from "@/types/localities";

export interface LocalityTarget {
  kind: "order" | "template";
  id: number;
}

/** What the card needs back from either PATCH. */
interface LocalityResponse {
  locality?: LocalityRef | null;
  locality_status?: string;
}

/**
 * Sets (`locality_id: <id>` → `manual`) or resets (`locality_id: null` →
 * re-resolved on the server) the locality of one order or permanent order —
 * Sprint 228, TASK-5176.
 *
 * No optimistic update: a reset resolves its status on the server and a set
 * recomputes the distance there, so a client-side guess would be wrong half the
 * time.
 *
 * The success toast is worded from the RESPONSE, not from the request: a reset
 * may come back `auto`, `ambiguous` or `not_found`, and only the server knows
 * which. Errors are left to the caller, which decides whether one belongs on
 * the field or in a toast.
 */
export function useSetLocality(target: LocalityTarget) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locality_id,
    }: {
      locality_id: number | null;
    }): Promise<LocalityResponse> =>
      target.kind === "order"
        ? updateOrder(target.id, { locality_id })
        : updateOrderTemplate(target.id, { locality_id }),
    onSuccess: async (response) => {
      const status = normaliseLocalityStatus(response.locality_status);
      const name = response.locality ? formatLocality(response.locality) : "";
      const message =
        status === "manual"
          ? t("orders.locality.toast.set", { name })
          : status === "auto"
            ? t("orders.locality.toast.resetAuto", { name })
            : status === "ambiguous"
              ? t("orders.locality.toast.resetAmbiguous", { name })
              : t("orders.locality.toast.resetNotFound");
      toastSuccess(message, undefined, {
        id: toastId("success", "locality", `${target.kind}:${target.id}`),
      });

      await Promise.all([
        // The open card: `["order", id]` for an order; the `["order-templates"]`
        // prefix covers `["order-templates","detail",id]` for a permanent order.
        queryClient.invalidateQueries({
          queryKey:
            target.kind === "order"
              ? ["order", target.id]
              : ["order-templates"],
        }),
        // The `["orders"]` prefix covers `["orders","unified",…]` (the list row
        // and its badge), `["orders","filter-counts",…]` and the planning
        // warning's count.
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        // The route-template editor's stop details, so its band follows.
        queryClient.invalidateQueries({ queryKey: ["routeTemplateStops"] }),
      ]);
    },
  });
}
